import type { Assign } from '@ark-ui/react'
import { ark } from '@ark-ui/react'
import { forwardRef } from 'react'
import { useEnhancedChildren } from '~/hooks/use-enhanced-children'
import { cn } from '~/utils/cn'
import type { InputTextSharedProps } from './input-text-root'

export type InputTextActionsProps = Assign<
  React.ComponentProps<typeof ark.div>,
  InputTextSharedProps & {
    readOnly?: boolean
    hasError?: boolean
    value?: string
    isActive?: boolean
    disabled?: boolean
  }
> & {
  children: React.ReactNode
}

export const InputTextActions = forwardRef<
  HTMLDivElement,
  InputTextActionsProps
>(
  (
    {
      children,
      size = 'ml',
      disabled,
      readOnly,
      hasError,
      value,
      isActive,
      ...props
    },
    ref
  ) => {
    const enhancedChildren = useEnhancedChildren(children, {
      targets: ['InputTextIcon', 'InputTextField', 'InputTextClearButton'],
      props: {},
    })

    return (
      <ark.div
        ref={ref}
        className={cn(
          'group flex w-[100%] items-center justify-between gap-onepulse py-threepulse duration-100 ease-in bg-forms-filled ring-forms-border-filled ring-quarterpulse overflow-hidden focus-within:ring-halfpulse focus-within:bg-forms-fill-focused min-w-[calc(var(--sizing-huge)_+_var(--sizing-tiny))]',
          {
            'h-medium rounded-form px-twopulse': size === 'md',
            'h-mediumlarge rounded-form px-threepulse': size === 'ml',
            'h-large rounded-form px-threepulse': size === 'lg',
            'h-extralarge rounded-largecontainer px-threepulse': size === 'xl',
            'cursor-not-allowed ring-forms-border-disabled ring-quarterpulse':
              disabled,
            'cursor-not-allowed bg-forms-fill-readonly ring-forms-border-readonly ring-quarterpulse':
              readOnly && !disabled,
            'bg-forms-fill-error ring-forms-border-error hover:ring-halfpulse':
              hasError && !!value && !isActive,
            'bg-forms-fill-error ring-forms-border-error ring-halfpulse hover:bg-forms-fill-error hover:ring-halfpulse':
              hasError && isActive,
            'bg-forms-fill ring-forms-border-error hover:bg-forms-fill-hovered hover:ring-bg-forms-hovered hover:ring-halfpulse':
              hasError && !value && !isActive,
          },
          // Compound variants
          !readOnly &&
            !disabled &&
            !hasError &&
            'hover:ring-halfpulse [&:not(:focus-within)]:has-[:placeholder-shown]:bg-forms-fill [&:not(:focus-within)]:has-[:placeholder-shown]:hover:bg-forms-fill-hovered',
          !hasError &&
            !readOnly &&
            !disabled &&
            'focus-within:ring-action-border-focused hover:ring-halfpulse [&:not(:focus-within)]:has-[:placeholder-shown]:ring-forms-border [&:not(:focus-within)]:has-[:placeholder-shown]:hover:ring-forms-border-hovered [&:not(:focus-within)]:has-[:placeholder-shown]:ring-none'
        )}
        aria-disabled={disabled}
        data-disabled={disabled}
        {...props}
      >
        {enhancedChildren}
      </ark.div>
    )
  }
)

InputTextActions.displayName = 'InputTextActions'
